import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, Clock, ChevronRight, ExternalLink,
  RefreshCw, AlertCircle, Link2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-1 — AgendaCard.
// Refator visual do CalendarPreview com header padronizado (ícone purple +
// label AGENDA caps + "Ver tudo →"), lista compacta com hora em emerald,
// pill de origem ("Google"), e empty state convidativo com CTA.
//
// Lógica de OAuth (status + events) preserva a do CalendarPreview pra não
// quebrar fluxo de conexão.

const formatEventTime = (dateStr, isAllDay) => {
  if (isAllDay) return "Dia todo";
  try { return format(parseISO(dateStr), "HH:mm", { locale: ptBR }); }
  catch { return dateStr; }
};

// Identidade visual por provider — Google cyan, Outlook roxo (mesma paleta
// da tela de Conexões). Default google p/ retrocompat.
function providerMeta(provider) {
  const isOutlook = String(provider || "google").toLowerCase() === "outlook";
  return isOutlook
    ? { label: "Outlook", badgeClass: "text-brand-purple border-brand-purple/40" }
    : { label: "Google", badgeClass: "text-brand-cyan border-brand-cyan/40" };
}

export default function AgendaCard({ userId = "default_user", onConnectClick, onSeeAllClick }) {
  const [status, setStatus] = useState({ loading: true, connected: false, configured: false });
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState(null);

  const checkStatus = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/api/auth/google/status?user_id=${userId}`);
      if (r.ok) {
        const data = await r.json();
        setStatus({ loading: false, connected: data.connected, configured: data.configured });
        return data.connected;
      }
    } catch (_) { /* ignore */ }
    setStatus(prev => ({ ...prev, loading: false }));
    return false;
  }, [userId]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setError(null);
    try {
      const today = new Date().toISOString().split("T")[0];
      const r = await authFetch(`${API_URL}/api/calendar/events?user_id=${userId}&date=${today}`);
      if (r.ok) {
        const data = await r.json();
        setEvents(data.events || []);
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || "Erro ao carregar eventos");
      }
    } catch (_) {
      setError("Falha ao conectar com o servidor");
    } finally {
      setLoadingEvents(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const connected = await checkStatus();
      if (!cancelled && connected) await loadEvents();
    })();
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "true") {
      checkStatus().then(c => { if (c && !cancelled) loadEvents(); });
    }
    return () => { cancelled = true; };
  }, [checkStatus, loadEvents]);

  const handleConnect = async () => {
    if (onConnectClick) return onConnectClick();
    try {
      const r = await authFetch(`${API_URL}/api/auth/google/start?user_id=${userId}`);
      if (r.ok) {
        const data = await r.json();
        window.location.href = data.authorization_url;
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || "Falha ao iniciar conexão");
      }
    } catch (_) { setError("Erro de conexão"); }
  };

  const Header = ({ children }) => (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-purple/10 flex items-center justify-center">
          <Calendar className="w-4 h-4 text-brand-purple" aria-hidden="true" />
        </div>
        <span
          className="text-caption font-semibold uppercase text-brand-purple/80"
          style={{ letterSpacing: "0.12em" }}
        >
          Agenda
        </span>
      </div>
      {children}
    </div>
  );

  // Loading shell — match final card height pra evitar layout shift.
  if (status.loading) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[280px]" data-testid="agenda-card">
        <Header />
        <div className="flex items-center gap-2 text-foreground-muted">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-caption">Carregando agenda…</span>
        </div>
      </Card>
    );
  }

  // Não configurado
  if (!status.configured) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[280px]" data-testid="agenda-card">
        <Header />
        <div className="flex items-center gap-2 text-status-warning bg-status-warning/10 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          <span className="text-caption">Aguardando configuração</span>
        </div>
      </Card>
    );
  }

  // Não conectado — empty state convidativo
  if (!status.connected) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[280px] flex flex-col" data-testid="agenda-card">
        <Header />
        <div className="flex-1 flex flex-col items-start justify-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-purple/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-brand-purple" aria-hidden="true" />
          </div>
          <h3 className="font-display font-semibold text-foreground text-h3">
            Conecte sua agenda
          </h3>
          <p className="text-caption text-foreground-muted max-w-xs">
            Conecte o Google Calendar pra ver os compromissos do dia direto aqui.
          </p>
          <Button
            variant="secondary"
            size="md"
            onClick={handleConnect}
            data-testid="agenda-connect-btn"
            className="mt-2"
          >
            <Link2 className="w-4 h-4" />
            Conectar Google Calendar
          </Button>
        </div>
      </Card>
    );
  }

  // Conectado — lista de compromissos do dia
  const visible = events.slice(0, 4);
  // Multi-conta: identifica a conta só quando há ≥2 contas distintas.
  const showAccount = new Set(
    events.map((e) => e.account_email).filter(Boolean)
  ).size >= 2;

  return (
    <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[280px] flex flex-col" data-testid="agenda-card">
      <Header>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadEvents}
            disabled={loadingEvents}
            className="p-1.5 rounded-md text-foreground-muted hover:text-brand-purple hover:bg-brand-purple/10 transition-colors disabled:opacity-50"
            aria-label="Atualizar agenda"
            data-testid="agenda-refresh"
          >
            <RefreshCw className={cn("w-4 h-4", loadingEvents && "animate-spin")} />
          </button>
          {onSeeAllClick && (
            <button
              type="button"
              onClick={onSeeAllClick}
              className="inline-flex items-center gap-1 text-caption text-foreground-muted hover:text-brand-purple transition-colors"
              data-testid="agenda-see-all"
            >
              Ver tudo
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </Header>

      {error && (
        <div className="flex items-center gap-2 text-status-error bg-status-error/10 px-3 py-2 rounded-lg mb-3">
          <AlertCircle className="w-4 h-4" />
          <span className="text-caption">{error}</span>
        </div>
      )}

      <div className="flex-1 flex flex-col">
        <AnimatePresence mode="popLayout">
          {events.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col items-start justify-center gap-3"
            >
              <div className="w-12 h-12 rounded-full bg-brand-purple/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-brand-purple" aria-hidden="true" />
              </div>
              <h3 className="font-display font-semibold text-foreground text-h3">
                Sem compromissos hoje
              </h3>
              <p className="text-caption text-foreground-muted">
                Aproveite o dia livre. Quer planejar?
              </p>
              {onSeeAllClick && (
                <Button variant="secondary" size="md" onClick={onSeeAllClick} className="mt-1" data-testid="agenda-empty-cta">
                  <Calendar className="w-4 h-4" />
                  Agendar
                </Button>
              )}
            </motion.div>
          ) : (
            <ul className="space-y-1" data-testid="agenda-list">
              {visible.map((event, idx) => (
                <motion.li
                  key={event.id || idx}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.25 }}
                  className="group"
                >
                  <a
                    href={event.html_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 px-3 py-3 rounded-lg hover:bg-surface-card-hover transition-colors"
                  >
                    <div className="w-14 shrink-0">
                      <div className="inline-flex items-center gap-1 text-brand-green font-mono text-sm tabular-nums">
                        <Clock className="w-3.5 h-3.5 opacity-70" />
                        {formatEventTime(event.start, event.is_all_day)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-brand-purple transition-colors">
                        {event.summary || "(sem título)"}
                      </p>
                      {showAccount && (event.connection_label || event.account_email) && (
                        <p className="text-caption text-foreground-subtle truncate">
                          {event.connection_label && event.account_email
                            ? `${event.connection_label} · ${event.account_email}`
                            : (event.connection_label || event.account_email)}
                        </p>
                      )}
                      {event.location && (
                        <p className="text-caption text-foreground-subtle truncate">
                          {event.location}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={cn("shrink-0 hidden md:inline-flex", providerMeta(event.provider).badgeClass)}
                    >
                      {providerMeta(event.provider).label}
                    </Badge>
                    <ExternalLink className="w-3.5 h-3.5 text-foreground-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </motion.li>
              ))}
            </ul>
          )}
        </AnimatePresence>

        {events.length > 4 && (
          <p className="text-micro text-foreground-subtle text-center mt-3 pt-2 border-t border-subtle">
            +{events.length - 4} mais compromissos
          </p>
        )}
      </div>
    </Card>
  );
}
